import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Empty, Grid, Image, Input, NavBar, Popup, SearchBar, Selector, Skeleton, Toast } from 'antd-mobile';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../../icon';
import { DINNER_PAY_OPTIONS, DINNER_STATUS, getDinnerStatusMeta, isDinnerHostDisabled } from '../dinnerStatus';
import '../DinnerInfo/index.css';
import './index.css';
import { useAuth } from '../../app/providers/AuthProvider';
import { dinnerApi } from '../../features/dinner/api/dinnerApi';
import { happyEventApi } from '../../features/happy-event/api/happyEventApi';
import { memberApi } from '../../features/member/api/memberApi';

const statusOptions = DINNER_PAY_OPTIONS;

const memberTypeMeta = (tempMember) => {
    if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
    if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
    if (Number(tempMember) === 4) return { label: '离队', order: 2 };
    if (Number(tempMember) === 1) return { label: '临时', order: 3 };
    return { label: '其他', order: 4 };
};

const normalizeDateText = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
};

const isDateExpired = (value) => {
    const dateText = normalizeDateText(value);
    if (!dateText) return false;
    // 聚餐当天仍允许普通用户操作，只有当前日期晚于聚餐日期才锁定。
    const [year, month, day] = dateText.split('-').map(Number);
    if (!year || !month || !day) return false;
    const dinnerDate = new Date(year, month - 1, day);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today.getTime() > dinnerDate.getTime();
};

function StatusChangeSelector({ options, onSelect }) {
    const [selected, setSelected] = useState(options[0]?.value);

    useEffect(() => {
        onSelect(options[0]?.value);
    }, [options, onSelect]);

    return (
        <Selector
            columns={2}
            value={[selected]}
            options={options}
            onChange={(value) => {
                setSelected(value[0]);
                onSelect(value[0]);
            }}
        />
    );
}

export default function DinnerDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { dinnerId: routeDinnerId } = useParams();
    const stateDinnerId = location.state?.dinnerId;
    const dinnerId = routeDinnerId || stateDinnerId;
    const { memberInfo } = useAuth();
    const isAdmin = Number(memberInfo?.admin) === 1;
    const [dinnerInfo, setDinnerInfo] = useState(null);
    const [memberList, setMemberList] = useState([]);
    const [happyList, setHappyList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addVisible, setAddVisible] = useState(false);
    const [addType, setAddType] = useState(1);
    const [addSelected, setAddSelected] = useState([]);
    const [editVisible, setEditVisible] = useState(false);
    const [editForm, setEditForm] = useState({ position: '', remark: '', status: 0, hostMemberIds: [] });
    const [pickerVisible, setPickerVisible] = useState(false);
    const [pickerMode, setPickerMode] = useState('add');
    const [pickerSearch, setPickerSearch] = useState('');

    const memberMap = useMemo(() => new Map(memberList.map(member => [String(member.id), member])), [memberList]);
    const existingMemberIds = useMemo(() => new Set((dinnerInfo?.members || []).map(item => String(item.memberId))), [dinnerInfo]);

    useEffect(() => {
        // 旧路由进入后转换为包含聚餐 ID 的正式地址。
        if (!routeDinnerId && stateDinnerId) {
            navigate(`/dinners/${stateDinnerId}`, { replace: true });
        }
    }, [navigate, routeDinnerId, stateDinnerId]);

    const mainImage = (member) => {
        const image = member?.memberImageList?.find(item => item.type === 0)?.image;
        return image ? image + Icon.ImageFooter : Icon.KEPENG_LOGO;
    };

    const loadMembers = useCallback((detail) => {
        const params = detail?.gameId ? { gameId: detail.gameId } : {};
        return memberApi.getGameMembers(params).then((response) => {
            const data = response.data?.data || [];
            // 聚餐详情统一使用比赛成员接口，这个接口会返回人员默认头像数据。
            const records = Array.isArray(data) && data[0]?.items ? data.flatMap(group => group.items || []) : data;
            const nextMembers = (Array.isArray(records) ? records : []).map(member => {
                const typeMeta = memberTypeMeta(member.tempMember);
                return { ...member, typeLabel: typeMeta.label, typeOrder: typeMeta.order };
            }).sort((a, b) => a.typeOrder - b.typeOrder || String(a.name || '').localeCompare(String(b.name || ''), 'zh-Hans-CN'));
            setMemberList(nextMembers);
        });
    }, []);

    const loadDinnerDetail = useCallback(() => {
        if (!dinnerId) {
            setDinnerInfo(null);
            return Promise.resolve();
        }
        return dinnerApi.getDetail(dinnerId).then((response) => {
            if (response.data?.code === 200) {
                const detail = response.data?.data || null;
                setDinnerInfo(detail);
                return detail;
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '聚餐详情加载失败' });
            }
            return null;
        });
    }, [dinnerId]);

    const loadHappyList = useCallback(() => {
        if (!dinnerId) return Promise.resolve();
        return happyEventApi.getList({ current: 1, size: 10, dinnerId, status: 0 }).then((response) => {
            setHappyList(response.data?.data?.records || []);
        });
    }, [dinnerId]);

    const reloadAll = useCallback(() => {
        setLoading(true);
        loadDinnerDetail()
            .then((detail) => Promise.all([loadMembers(detail), loadHappyList()]))
            .catch(() => Toast.show({ icon: 'fail', content: '聚餐详情加载失败' }))
            .finally(() => setLoading(false));
    }, [loadDinnerDetail, loadHappyList, loadMembers]);

    useEffect(() => {
        reloadAll();
    }, [reloadAll]);

    const joinDinner = (type, memberIds) => {
        if (!isAdmin && isDateExpired(dinnerInfo?.dinnerDate || dinnerInfo?.createTime)) {
            return Toast.show({ content: '聚餐时间已过，无法再操作' });
        }
        const ids = Array.isArray(memberIds) ? memberIds : [memberIds];
        if (!ids.filter(Boolean).length) return Toast.show({ content: '请选择人员' });
        Toast.show({ icon: 'loading', duration: 0 });
        const formData = new FormData();
        formData.append('dinnerId', dinnerInfo.id);
        formData.append('type', type);
        formData.append('memberId', ids.join(','));
        return dinnerApi.joinById(formData).then((response) => {
            Toast.clear();
            if (response.data?.code === 200) {
                Toast.show({ icon: 'success', content: '操作成功' });
                setAddVisible(false);
                setPickerVisible(false);
                setAddSelected([]);
                return loadDinnerDetail();
            }
            Toast.show({ icon: 'fail', content: response.data?.msg || '操作失败' });
        }).catch(() => {
            Toast.clear();
            Toast.show({ icon: 'fail', content: '操作失败' });
        });
    };

    const openEdit = () => {
        setEditForm({
            position: dinnerInfo.position || '',
            remark: dinnerInfo.remark || '',
            status: dinnerInfo.status ?? DINNER_STATUS.PLAYER_HOST,
            hostMemberIds: (dinnerInfo.members || []).filter(item => item.type === 0).map(item => item.memberId)
        });
        setEditVisible(true);
    };

    const saveEdit = () => {
        if (!editForm.position.trim()) return Toast.show({ content: '请输入聚餐地址' });
        if (!isDinnerHostDisabled(editForm.status) && !editForm.hostMemberIds.length) return Toast.show({ content: '请选择请客人员' });
        Toast.show({ icon: 'loading', duration: 0 });
        dinnerApi.update({
            dinnerId: dinnerInfo.id,
            position: editForm.position.trim(),
            remark: editForm.remark.trim(),
            status: editForm.status,
            hostMemberIds: !isDinnerHostDisabled(editForm.status) ? editForm.hostMemberIds : []
        }).then((response) => {
            Toast.clear();
            if (response.data?.code === 200) {
                Toast.show({ icon: 'success', content: '修改成功' });
                setEditVisible(false);
                loadDinnerDetail();
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '修改失败' });
            }
        }).catch(() => {
            Toast.clear();
            Toast.show({ icon: 'fail', content: '修改失败' });
        });
    };

    const openMemberStatusDialog = (member, currentType) => {
        if (!isAdmin) return;
        const options = [
            { label: '改为请客', value: 0, show: currentType !== 0 && !isDinnerHostDisabled(dinnerInfo.status) },
            { label: '改为参加', value: 1, show: currentType !== 1 },
            { label: '改为暂定', value: 2, show: currentType !== 2 },
            { label: '取消记录', value: 3, show: true },
        ].filter(item => item.show);
        let nextType = options[0]?.value;
        Dialog.confirm({
            title: `修改 ${member.name}`,
            content: (
                <StatusChangeSelector
                    options={options}
                    onSelect={(value) => { nextType = value; }}
                />
            ),
            onConfirm: () => joinDinner(nextType, member.id)
        });
    };

    const openAddMembers = () => {
        setAddType(1);
        setAddSelected([]);
        setPickerSearch('');
        setAddVisible(true);
    };

    const openPicker = (mode) => {
        setPickerMode(mode);
        setPickerSearch('');
        setPickerVisible(true);
    };

    const pickerSelected = pickerMode === 'editHost' ? editForm.hostMemberIds : addSelected;
    const pickerBaseList = useMemo(() => {
        const base = pickerMode === 'editHost' ? memberList : memberList.filter(member => !existingMemberIds.has(String(member.id)));
        const keyword = pickerSearch.trim().toLowerCase();
        if (!keyword) return base;
        return base.filter(member => String(member.name || '').toLowerCase().includes(keyword));
    }, [existingMemberIds, memberList, pickerMode, pickerSearch]);

    const togglePickerMember = (memberId) => {
        const updater = (prev) => prev.some(id => String(id) === String(memberId))
            ? prev.filter(id => String(id) !== String(memberId))
            : [...prev, memberId];
        if (pickerMode === 'editHost') {
            setEditForm(prev => ({ ...prev, hostMemberIds: updater(prev.hostMemberIds) }));
        } else {
            setAddSelected(prev => updater(prev));
        }
    };

    const hostLabel = editForm.hostMemberIds.map(id => memberMap.get(String(id))?.name).filter(Boolean).join('、') || '请选择请客人员';
    const dinnerDateValue = dinnerInfo?.dinnerDate || dinnerInfo?.createTime;
    const dinnerExpired = isDateExpired(dinnerDateValue);
    const dinnerDateText = normalizeDateText(dinnerDateValue) || '日期未记录';
    const currentUserDinnerType = dinnerInfo?.members?.find(item => String(item.memberId) === String(memberInfo?.id))?.type;
    const statusMeta = getDinnerStatusMeta(dinnerInfo?.status);

    const renderMemberList = (type) => {
        const members = dinnerInfo?.members?.filter(item => item.type === type) || [];
        if (!members.length) return <div className="standalone-dinner-empty">暂无人选</div>;
        return (
            <Grid columns={5} gap={10}>
                {members.map(item => {
                    const member = memberMap.get(String(item.memberId));
                    return member ? (
                        <Grid.Item key={`${type}-${item.memberId}`} className="dinner-member-item" onClick={() => openMemberStatusDialog(member, type)}>
                            <div className="dinner-member-avatar-wrapper">
                                <Image src={mainImage(member)} width={42} height={42} fit="cover" className={`dinner-member-avatar ${type === 0 ? 'host' : ''}`} />
                            </div>
                            <span className="dinner-member-name">{member.name}</span>
                        </Grid.Item>
                    ) : null;
                })}
            </Grid>
        );
    };

    if (loading) {
        return (
            <div className="dinner-info-scope standalone-dinner-page has-unified-nav">
                <NavBar className="unified-navbar fixed" onBack={() => navigate(-1)}>聚餐详情</NavBar>
                <div className="standalone-dinner-skeleton"><Skeleton.Title animated /><Skeleton.Paragraph lineCount={8} animated /></div>
            </div>
        );
    }

    if (!dinnerInfo) {
        return (
            <div className="dinner-info-scope standalone-dinner-page has-unified-nav">
                <NavBar className="unified-navbar fixed" onBack={() => navigate(-1)}>聚餐详情</NavBar>
                <Empty description="未找到聚餐信息" style={{ marginTop: '30vh' }} />
            </div>
        );
    }

    return (
        <div className="dinner-info-scope standalone-dinner-page has-unified-nav">
            <NavBar className="unified-navbar fixed" onBack={() => navigate(-1)}>聚餐详情</NavBar>

            <section className="standalone-dinner-hero">
                <span>HUAXI SOCCER DINNER</span>
                <h1>{dinnerInfo.position || '聚餐地点'}</h1>
                <p>{dinnerDateText}</p>
            </section>

            <div className="standalone-action-row">
                {!memberInfo?.temp && dinnerInfo.status !== 2 && (!dinnerExpired || isAdmin) && (
                    <>
                        <button
                            className={`join ${currentUserDinnerType === 1 ? 'active' : ''}`}
                            disabled={currentUserDinnerType === 1}
                            onClick={() => joinDinner(1, memberInfo.id)}
                        >
                            报名
                        </button>
                        <button
                            className={`pending ${currentUserDinnerType === 2 ? 'active' : ''}`}
                            disabled={currentUserDinnerType === 2}
                            onClick={() => joinDinner(2, memberInfo.id)}
                        >
                            暂定
                        </button>
                        <button
                            className={`muted ${currentUserDinnerType === undefined ? 'disabled' : ''}`}
                            disabled={currentUserDinnerType === undefined}
                            onClick={() => joinDinner(3, memberInfo.id)}
                        >
                            取消
                        </button>
                    </>
                )}
                {!memberInfo?.temp && dinnerInfo.status !== 2 && dinnerExpired && !isAdmin && (
                    <div className="standalone-dinner-expired">聚餐时间已过，报名和暂定已关闭</div>
                )}
                {isAdmin && (
                    <>
                        <button className="admin" onClick={openAddMembers}>添加人员</button>
                        <button className="admin" onClick={openEdit}>修改信息</button>
                    </>
                )}
            </div>

            {happyList.length > 0 && (
                <section className="standalone-happy-link" onClick={() => navigate('/happyEvent', { state: { happyId: happyList[0].id } })}>
                    <span>关联喜事</span>
                    <strong>{happyList[0].title}</strong>
                    <i>去查看</i>
                </section>
            )}

            <div className="dinner-detail-wrapper standalone-dinner-content">
                <div className="dinner-venue-card">
                    <div className="dinner-location-info">
                        <div className="dinner-location-title">🍽️ {dinnerInfo.position || '聚餐地点'}</div>
                        <div className="dinner-location-address">{dinnerDateText}</div>
                    </div>
                </div>

                {dinnerInfo.status === 2 ? (
                    <div className="dinner-status-alert cancelled">
                        <div style={{ fontWeight: 800 }}>聚餐已取消</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>原因：{dinnerInfo.cancelInfo || '暂无说明'}</div>
                    </div>
                ) : (
                    <>
                        {dinnerInfo.remark && (
                            <div className="dinner-remark-card">
                                <span className="dinner-remark-label">聚餐备注信息</span>
                                <div className="dinner-remark-text">{dinnerInfo.remark}</div>
                            </div>
                        )}
                        {statusMeta.alert && (
                            <div className={`dinner-status-alert special ${statusMeta.className}`}><span>⭐</span> {statusMeta.alert}</div>
                        )}
                    </>
                )}

                {!isDinnerHostDisabled(dinnerInfo.status) && (
                    <div className="dinner-section">
                        <div className="dinner-section-header"><div className="dinner-section-title">👑 慷慨请客</div><div className="dinner-section-count">{dinnerInfo?.members?.filter(i => i.type === 0).length || 0}人</div></div>
                        {renderMemberList(0)}
                    </div>
                )}
                <div className="dinner-section">
                    <div className="dinner-section-header"><div className="dinner-section-title">⚽ 确认参加</div><div className="dinner-section-count">{dinnerInfo?.members?.filter(i => i.type === 1).length || 0}人</div></div>
                    {renderMemberList(1)}
                </div>
                <div className="dinner-section">
                    <div className="dinner-section-header"><div className="dinner-section-title">⏳ 待定中</div><div className="dinner-section-count">{dinnerInfo?.members?.filter(i => i.type === 2).length || 0}人</div></div>
                    {renderMemberList(2)}
                </div>
            </div>

            <Popup visible={addVisible} onMaskClick={() => setAddVisible(false)} bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22 }} style={{ '--z-index': 1000 }}>
                <div className="standalone-popup">
                    <div className="standalone-popup-head"><button onClick={() => setAddVisible(false)}>取消</button><strong>添加聚餐人员</strong><button onClick={() => joinDinner(addType, addSelected)}>确认</button></div>
                    <Selector columns={3} value={[addType]} options={[{ label: '请客', value: 0, disabled: isDinnerHostDisabled(dinnerInfo.status) }, { label: '参加', value: 1 }, { label: '暂定', value: 2 }]} onChange={(value) => setAddType(value[0])} />
                    <button className="standalone-picker-field" onClick={() => openPicker('add')}>{addSelected.length ? `已选 ${addSelected.length} 人` : '选择人员'}</button>
                </div>
            </Popup>

            <Popup visible={editVisible} onMaskClick={() => setEditVisible(false)} bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22 }} style={{ '--z-index': 1000 }}>
                <div className="standalone-popup">
                    <div className="standalone-popup-head"><button onClick={() => setEditVisible(false)}>取消</button><strong>修改聚餐信息</strong><button onClick={saveEdit}>保存</button></div>
                    <label>聚餐地址</label>
                    <Input className="standalone-input" value={editForm.position} onChange={(value) => setEditForm(prev => ({ ...prev, position: value }))} />
                    <label>备注</label>
                    <Input className="standalone-input" value={editForm.remark} onChange={(value) => setEditForm(prev => ({ ...prev, remark: value }))} />
                    <label>聚餐类型</label>
                    <Selector columns={2} value={[editForm.status]} options={statusOptions} onChange={(value) => setEditForm(prev => ({ ...prev, status: value[0], hostMemberIds: isDinnerHostDisabled(value[0]) ? [] : prev.hostMemberIds }))} />
                    {!isDinnerHostDisabled(editForm.status) && (
                        <button className="standalone-picker-field" onClick={() => openPicker('editHost')}>{hostLabel}</button>
                    )}
                </div>
            </Popup>

            <Popup visible={pickerVisible} onMaskClick={() => setPickerVisible(false)} bodyStyle={{ height: '72vh', borderTopLeftRadius: 22, borderTopRightRadius: 22, overflow: 'hidden' }} style={{ '--z-index': 2100 }}>
                <div className="standalone-member-picker">
                    <div className="standalone-popup-head"><button onClick={() => setPickerVisible(false)}>取消</button><strong>选择人员</strong><button onClick={() => setPickerVisible(false)}>确认</button></div>
                    <SearchBar placeholder="搜索姓名" value={pickerSearch} onChange={setPickerSearch} />
                    <div className="standalone-member-grid">
                        {pickerBaseList.map(member => {
                            const selected = pickerSelected.some(id => String(id) === String(member.id));
                            return (
                                <button key={member.id} className={selected ? 'active' : ''} onClick={() => togglePickerMember(member.id)}>
                                    <span>{member.name}</span>
                                    <em>{member.typeLabel}</em>
                                    {selected && <i>已选</i>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Popup>
        </div>
    );
}
